<script setup lang="ts">
import NavFlyout from "@/Components/Nav/NavFlyout.vue";
import NavFlyoutItem from "@/Components/Nav/NavFlyoutItem.vue";
import type { NavLinkWithHref } from "@/Types/NavLink";
import { vOnClickOutside } from "@vueuse/components";
import { OnClickOutsideOptions, useElementBounding } from "@vueuse/core";
import { type Component, computed, PropType, ref, watch } from "vue";

const props = defineProps({
    label: {
        type: String,
        default: "",
    },
    expanded: {
        type: Boolean,
        default: false,
    },
    icon: {
        type: Object as PropType<Component>,
        default: null,
    },
    iconActive: {
        type: Object as PropType<Component>,
        default: null,
    },
    active: {
        type: Boolean,
        default: false,
    },
    children: {
        type: Array as PropType<NavLinkWithHref[]>,
        default: () => [],
    },
});

const emit = defineEmits(["click-link"]);

const showingFlyout = ref(false);
const menuButton = ref<HTMLButtonElement>();

const closeFlyout = () => {
    showingFlyout.value = false;
};

const showFlyout = () => {
    showingFlyout.value = true;
};

function toggleShowFlyout() {
    if (showingFlyout.value) {
        closeFlyout();
    } else {
        showFlyout();
    }
}

const menuOpen = ref(false);

watch(
    () => props.expanded,
    (expanded) => {
        if (!expanded) {
            menuOpen.value = false;
        }
    },
);

const classes = computed(() => (props.active ? "bg-gray-300 font-semibold" : ""));

const { top: buttonTop, width: buttonWidth } = useElementBounding(menuButton);

const flyoutStyle = computed(() => ({
    top: `${buttonTop.value}px`,
    left: `${buttonWidth.value + 16}px`,
}));

const onClickOutsideHandler: [(evt: Event) => void, OnClickOutsideOptions] = [
    () => {
        if (showingFlyout.value) {
            showingFlyout.value = false;
        }
    },
    { ignore: [menuButton] },
];

const handleClickFlyoutItem = () => {
    closeFlyout();
    emit("click-link");
};

const iconClasses = computed(() => (props.active ? "fill-white bg-gray-900" : "fill-gray-900 bg-transparent"));

const navIcon = computed(() => (props.active ? props.iconActive : props.icon));
</script>

<template>
    <div class="">
        <button
            class="block h-12 w-full overflow-clip rounded-full p-1 hover:bg-gray-400"
            @click.stop="toggleShowFlyout"
            ref="menuButton"
            :class="classes"
        >
            <span class="flex items-center justify-start gap-x-3">
                <span
                    class="flex h-10 w-10 flex-none items-center justify-center rounded-full"
                    :class="iconClasses"
                >
                    <Component
                        :is="navIcon"
                        class="h-5"
                    />
                </span>

                <span
                    class="flex-none transition-all"
                    :class="{ '': expanded, 'opacity-0': !expanded }"
                >
                    {{ label }}
                </span>
            </span>
        </button>

        <NavFlyout
            v-if="showingFlyout"
            class="absolute z-20 flex flex-col"
            :style="flyoutStyle"
            v-on-click-outside="onClickOutsideHandler"
        >
            <NavFlyoutItem
                v-for="child in children"
                :key="child.href"
                :href="child.href"
                :icon="child.icon"
                :label="child.label"
                @click="handleClickFlyoutItem"
            />
        </NavFlyout>
    </div>
</template>
